import queryString from 'query-string';
import type { LoginErrorCode } from '../types';
import type { components } from '../generated/sign-in-service-openapi';
import {
  captureTaggedException,
  addBreadcrumb,
  setContext,
} from '../utilities/analytics/error-reporting';
import { State } from '../reducers';

import {
  Action,
  CheckUserNamePasswordlessType,
  CheckUserNameRedirectType,
  CheckUserNameSocialType,
  Dispatch,
  JWT,
  LoginFieldError,
  LoginGlobalError,
  LoginState,
  ThunkAction,
  URLParameters,
  UserFlow,
} from '../types';
import { buildMarketingConsent, ConsentCheckboxState } from '../utilities/marketing-consent';
import { AnalyticsClient, EventAttributes } from '../utilities/analytics/analytics-web-client';
import { loginPageId } from '../pages/login/constants';
import { MarketingConsentConfig } from '../reducers/marketing-consent-reducer';
import { login } from '../utilities/login/atlassian-authentication';
import { updateCsrfToken } from './csrf-token-actions';
import { ChallengeResponse } from '../contexts/recaptcha';
import { toChallengeRequest } from '../utilities/recaptcha';
import { getAwsCaptchaTokenValue, isValidAwsWafConfig } from '../aws-captcha/aws-captcha.helpers';
import { checkFeatureGate } from '../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';

/**
 NOTE: the pattern here is to combine types auto-generated from OpenAPI specs (in sign-in-service),
 with manually defined types to model the backend responses correctly.

The OpenAPI specs do a good job of exhaustively documenting fields and values in the backend
response.

But they do not model specific response variants accurately (which fields are present/absent under
which specific shapes).

This kind of strictness is better captured by manually modeling the responses as discriminated
unions.

We take a hybrid approach where: 1. discriminated unions strictly model which fields are present
when, and 2. auto-generated types are referenced for the specific fields in each union
*/

type CheckUsernameV2ResponseItem = components['schemas']['check-username-v2-response'][number];
type CheckUsernameV2Action = CheckUsernameV2ResponseItem['action'];
type CheckUsernameV2RedirectUri = NonNullable<CheckUsernameV2ResponseItem['redirect_uri']>;
type CheckUsernameV2IcId = NonNullable<CheckUsernameV2ResponseItem['ic_id']>;
type CheckUsernameV2IcLabel = NonNullable<CheckUsernameV2ResponseItem['ic_label']>;
type CheckUsernameV2Identities = NonNullable<CheckUsernameV2ResponseItem['identities']>;

type Identities = {
  google?: NonNullable<CheckUsernameV2Identities['google']>;
  microsoft?: NonNullable<CheckUsernameV2Identities['microsoft']>;
  apple?: NonNullable<CheckUsernameV2Identities['apple']>;
  slack?: NonNullable<CheckUsernameV2Identities['slack']>;
  password: NonNullable<CheckUsernameV2Identities['password']>;
};

// V2 OpenAPI says `redirect_type` is only `sso`, but this manual action model is still
// shared with v1 check-username handling through Redux state/actions. v1 can still return
// `email_sent`, so keep the shared redirect type broad until v1/v2 handling is separated.
export interface RedirectUserAction {
  action: Extract<CheckUsernameV2Action, 'redirect'>;
  redirect_uri: CheckUsernameV2RedirectUri;
  redirect_type: CheckUserNameRedirectType;
}

export interface IsolatedCloudRedirectUserAction {
  action: Extract<CheckUsernameV2Action, 'ic_redirect'>;
  ic_id: CheckUsernameV2IcId;
  ic_label: CheckUsernameV2IcLabel;
  redirect_uri: CheckUsernameV2RedirectUri;
}

export type ContinueRequiredAction = {
  action: Extract<CheckUsernameV2Action, 'continue_required'>;
};

export interface SignupUserAction {
  action: Extract<CheckUsernameV2Action, 'signup'>;
}

export interface NoUserAction {
  action: Extract<CheckUsernameV2Action, 'no_action'>;
  passwordless?: CheckUserNamePasswordlessType;
  social?: CheckUserNameSocialType;
}

export interface SocialLoginAction {
  action: Extract<CheckUsernameV2Action, 'social_login'>;
  passwordless?: CheckUserNamePasswordlessType;
  identities: Identities;
}

export interface NotPermittedAction {
  action: Extract<CheckUsernameV2Action, 'not_permitted'>;
  error_code: LoginErrorCode;
}

export type UserAction =
  | RedirectUserAction
  | ContinueRequiredAction
  | SignupUserAction
  | NoUserAction
  | SocialLoginAction
  | IsolatedCloudRedirectUserAction
  | NotPermittedAction;

export const rejectNonOkResponse = (response: Response): Response | Promise<any> =>
  response.ok
    ? response
    : response.text().then(error => {
        throw new Error(error || response.statusText);
      });

export const requestCheckUsername = (): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME',
});

export const requestCheckUsernameRedirect = (userAction: RedirectUserAction): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_REDIRECT',
  redirectUrl: userAction.redirect_uri,
  redirectType: userAction.redirect_type,
});

export const requestIsolatedCloudRedirect = (
  userAction: IsolatedCloudRedirectUserAction
): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_ISOLATED_CLOUD_REDIRECT',
  redirect_uri: userAction.redirect_uri,
});

export const requestIsolatedCloudCheckUsername = (): Action => ({
  type: 'LOGIN_REQUEST_ISOLATED_CLOUD_CHECK_USERNAME',
});

export const requestIsolatedCloudCheckUsernameSucceeded = (userActions: UserAction[]): Action => ({
  type: 'LOGIN_REQUEST_ISOLATED_CLOUD_CHECK_USERNAME_SUCCEEDED',
  userActions,
});

export const requestCheckUsernameSignup = (): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_SIGNUP',
});

export const requestCheckUsernameNoAction = (userAction: NoUserAction): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_NO_ACTION',
  passwordless: userAction.passwordless || CheckUserNamePasswordlessType.Disallowed,
  social: userAction.social || 'allowed',
});

export const selectCheckUsernameAccount = (userAction: UserAction): Action => ({
  type: 'LOGIN_SELECT_CHECK_USERNAME_USER_ACTION',
  userAction,
});

export const requestCheckUsernameSocialLogin = (userAction: SocialLoginAction): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_SOCIAL_LOGIN',
  passwordless: userAction.passwordless || CheckUserNamePasswordlessType.Disallowed,
  identities: userAction.identities,
});

export const requestCheckUsernameSucceeded =
  (usernameCheckParams: UsernameCheckParams, userAction: UserAction): ThunkAction =>
  dispatch => {
    switch (userAction.action) {
      case 'redirect':
        dispatch(requestCheckUsernameRedirect(userAction));
        break;
      case 'signup':
        dispatch(requestCheckUsernameSignup());
        break;
      case 'no_action':
        dispatch(requestCheckUsernameNoAction(userAction));
        break;
      case 'social_login':
        if (!userAction.identities || userAction.identities.password === undefined) {
          // this event is needed for growth team's experiment slo
          // we want to silently fire this event and not show any error
          // this will just not enrol user in the experiment
          usernameCheckParams.analyticsClient.operationalEvent({
            page: loginPageId,
            action: 'loadSocialLoginPage',
            subject: 'sli',
            attributes: {
              hasError: true,
              errorName: 'identities not as expected',
            },
          });
        }
        dispatch(requestCheckUsernameSocialLogin(userAction));
        break;
      case 'continue_required':
        dispatch(isolatedCloudCheckUsernameContinueRequired(usernameCheckParams));
        break;
      case 'ic_redirect':
        dispatch(requestIsolatedCloudRedirect(userAction));
        break;
      case 'not_permitted':
        dispatch(requestCheckUsernameError({ code: userAction.error_code }));
        break;
    }
    return Promise.resolve();
  };

export const checkUsernameCaptchaFailure = (isOnline: boolean) =>
  requestCheckUsernameError({ code: isOnline ? 'captcha' : 'request_error' });

export const requestCheckUsernameFailed = (error: LoginFieldError['email']): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_FAILED',
  error,
});

export const requestCheckUsernameError = (error: LoginGlobalError): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_ERROR',
  error,
});

export const resetLoginForm = (): Action => ({
  type: 'LOGIN_RESET',
});

export const resetRememberMeState = (): Action => ({
  type: 'RESET_REMEMBER_ME_STATE',
});

export const requestUsernamePasswordLogin = (): Action => ({
  type: 'LOGIN_REQUEST_USERNAME_PASSWORD_LOGIN',
});

export const requestPasswordlessLogin = (): Action => ({
  type: 'LOGIN_REQUEST_PASSWORDLESS_LOGIN',
});

export const requestPasswordlessSucceeded = (): Action => ({
  type: 'LOGIN_REQUEST_PASSWORDLESS_LOGIN_SUCCESS',
});

export const requestPasswordlessFailed = (error: LoginGlobalError): Action => ({
  type: 'LOGIN_REQUEST_PASSWORDLESS_LOGIN_FAILED',
  error,
});

export const requestUsernamePasswordLoginSucceeded = (redirectUrl = ''): Action => ({
  type: 'LOGIN_REQUEST_USERNAME_PASSWORD_LOGIN_SUCCESS',
  redirectUrl,
});

export const requestUsernamePasswordLoginFailed = (error: LoginGlobalError): Action => ({
  type: 'LOGIN_REQUEST_USERNAME_PASSWORD_LOGIN_FAILED',
  error: error,
});

export const redirectToUserInactivePage = (redirectUrl: string): Action => ({
  type: 'LOGIN_REDIRECT_USER',
  redirectUrl,
});

export const updateLoginAttemptDetails = (nextAttempt: number): Action => ({
  type: 'UPDATE_LOGIN_ATTEMPT_DETAILS',
  nextAttempt,
});

export const updateCaptchaRequirement = (isCaptchaRequired: boolean): Action => ({
  type: 'LOGIN_UPDATE_CAPTCHA_REQUIREMENT',
  isCaptchaRequired,
});

export const showFormErrors = (errors: LoginFieldError): Action => ({
  type: 'LOGIN_SHOW_LOGIN_FORM_ERRORS',
  errors,
});

export const setRememberMeCheckbox = (checked: boolean): Action => ({
  type: 'REMEMBER_ME_CHECKBOX_UPDATE',
  payload: {
    enabled: checked,
  },
});

export const toggleRememberMeCheckbox = (): Action => ({
  type: 'REMEMBER_ME_CHECKBOX_TOGGLE',
});

type OnShowAwsCaptchaModalCallbackProps = {
  /** Function called when the puzzle is successfully completed. */
  onSuccess: () => void;
};

export type UsernameCheckParams = {
  /** The username provided to the check-username endpoint to determine how to handle this user. */
  username: string;
  /** The URL query parameters to include in the call to `/rest/check-username`. _MUST_ include the leading ? character if the query params has any content */
  params: string;
  analyticsClient: AnalyticsClient;
  recoveryToken?: JWT;
  challengeResponse?: ChallengeResponse | null;
  destinationOrgId?: string;
  /** A callback that renders the AWS Captcha puzzle. Used when a 405 is returned from the WAF which indicates that the user is required to complete a captcha puzzle. */
  onShowAwsCaptchaModal: ({ onSuccess }: OnShowAwsCaptchaModalCallbackProps) => void;
};

type AnalyticsFailureAttributes =
  | (EventAttributes & {
      reason: 'http_error';
      httpStatus: number;
      description?: string;
    })
  | (EventAttributes & {
      reason: 'response_parsing_error' | 'unknown';
      description?: string;
    });

const usernameCheck =
  ({
    username,
    params,
    analyticsClient,
    recoveryToken,
    challengeResponse,
    destinationOrgId,
    onShowAwsCaptchaModal,
  }: UsernameCheckParams): ThunkAction =>
  async (dispatch, getState) => {
    dispatch(requestCheckUsername());

    // Whether the AWS Captcha feature is enabled
    const isAwsCaptchaEnabled = checkFeatureGate(AidFrontendFeatureGate.ENABLE_AWS_WAF_CAPTCHA);

    // Whether we want to include the captcha headers for the `/rest/check-username` call - used to test the integration E2E.
    const shouldIncludeTestCaptchaHeaders = checkFeatureGate(
      AidFrontendFeatureGate.ENABLE_HOST_HEADERS_FOR_AWS_CAPTCHA
    );

    addBreadcrumb({
      category: 'login.check_username',
      message: 'Check username API call initiated',
      level: 'info',
      data: {
        hasRecoveryToken: !!recoveryToken,
        hasChallengeResponse: !!challengeResponse,
        challengeType: challengeResponse?.type,
      },
    });

    let response: Response;
    try {
      const awsCaptchaToken = isAwsCaptchaEnabled ? getAwsCaptchaTokenValue() : '';

      response = await fetch(`/rest/check-username${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getState().csrfToken,

          // This header triggers the WAF to trigger a captcha (meaning that the response will be a 405)
          ...(shouldIncludeTestCaptchaHeaders
            ? {
                'test-captcha': 'true',
              }
            : {}),

          // The token parameter is only included if the user has a token in their cookies
          // The token exists only if the AWS Captcha SDK is included/loaded.
          // The token will never exist in environments/boundaries that do not include the SDK (e.g. commercial)
          ...(isAwsCaptchaEnabled
            ? {
                'aws-waf-token': awsCaptchaToken,
              }
            : {}),
        },
        body: JSON.stringify({
          username,
          recoveryToken,
          'challenge-response': toChallengeRequest(challengeResponse),
          destinationOrgId,
        }),
      });
    } catch (networkError) {
      addBreadcrumb({
        category: 'login.check_username',
        message: 'Network error during check-username',
        level: 'error',
        data: { isOnline: navigator.onLine },
      });

      captureTaggedException(networkError, {
        capabilities: [
          'capability-group--login',
          'capability-subgroup--username-password-login',
          'capability-name--login-with-username-password',
        ],
        extraProperties: {
          description: 'Network failure during check-username API call',
          isOnline: navigator.onLine,
          endpoint: '/rest/check-username',
        },
      });

      dispatch(requestCheckUsernameError({ code: 'request_error' }));
      return;
    }

    // 405 is returned only by AWS Captcha in the situation where the WAF is asking the user to complete a captcha.
    // We also require config to be present — if the gate is on but config hasn't arrived from the backend yet,
    // we can't render the puzzle, so we fall through to normal error handling rather than showing an empty modal.
    const awsWafConfig = getState().appConfig.awsWafConfig;
    const isAwsCaptchaConfigured = isValidAwsWafConfig(awsWafConfig);
    if (isAwsCaptchaEnabled && isAwsCaptchaConfigured && response.status === 405) {
      addBreadcrumb({
        category: 'login.show_captcha',
        message: 'Captcha shown for check-username',
        level: 'info',
      });
      onShowAwsCaptchaModal({
        onSuccess: () => {
          // When the user completes the puzzle, we want to retry the request
          dispatch(
            usernameCheck({
              username,
              params,
              analyticsClient,
              recoveryToken,
              challengeResponse,
              destinationOrgId,
              onShowAwsCaptchaModal,
            })
          );

          addBreadcrumb({
            category: 'login.show_captcha.puzzle_completed',
            message: 'Captcha completed',
            level: 'info',
          });
        },
      });
      return;
    }

    let analyticsAction: 'checkUsernameSuccessful' | 'checkUsernameFailure';
    let analyticsAttributes = {} as AnalyticsFailureAttributes;

    if (response.ok) {
      try {
        const userAction: UserAction = await response.json();

        dispatch(
          requestCheckUsernameSucceeded(
            {
              username,
              params,
              analyticsClient,
              recoveryToken,
              challengeResponse,
              onShowAwsCaptchaModal,
            },
            userAction
          )
        );

        // Set context with auth method details
        setContext('login.checkUsername', {
          action: userAction?.action,
          redirectType: userAction?.action === 'redirect' && userAction?.redirect_type,
          passwordless: userAction?.action === 'no_action' && userAction?.passwordless,
          social: userAction?.action === 'no_action' && userAction?.social,
          hasRecoveryToken: !!recoveryToken,
          challengeType: challengeResponse?.type || null,
        });

        addBreadcrumb({
          category: 'login.check_username',
          message: 'Check username succeeded',
          level: 'info',
          data: {
            action: userAction.action,
            redirectType: userAction.action === 'redirect' && userAction.redirect_type,
          },
        });

        analyticsAction = 'checkUsernameSuccessful';
      } catch (parseError) {
        dispatch(requestCheckUsernameError({ code: 'unknown' }));
        analyticsAction = 'checkUsernameFailure';
        analyticsAttributes = {
          ...analyticsAttributes,
          reason: 'response_parsing_error',
        };
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Check-username response OK but failed to parse it',
          },
        });
      }
    } else {
      analyticsAction = 'checkUsernameFailure';
      let responseErrorText: string | null = null;

      addBreadcrumb({
        category: 'login.check_username',
        message: 'Check username HTTP error',
        level: 'error',
        data: { statusCode: response.status },
      });

      try {
        analyticsAttributes = {
          ...analyticsAttributes,
          reason: 'http_error',
          httpStatus: response.status,
        };

        if (response.status === 429) {
          dispatch(requestCheckUsernameError({ code: 'too_many_username_requests' }));
        } else if (response.status === 400) {
          const { uiErrors } = await response.json();
          if (uiErrors && uiErrors.length) {
            dispatch(requestCheckUsernameError({ code: uiErrors[0].key || 'unknown' }));
          }
        } else {
          responseErrorText = (await response.text()) || response.statusText;
          const extractedError: LoginFieldError['email'] =
            JSON.parse(responseErrorText).formErrors[0];
          analyticsAttributes = {
            ...analyticsAttributes,
            description: extractedError,
          };
          dispatch(requestCheckUsernameFailed(extractedError));
        }
      } catch (error) {
        const description = 'Failed to handle non-OK response from check-username';
        captureTaggedException(error, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description,
            responseStatus: response.status,
            responseErrorText,
          },
        });
        analyticsAttributes = {
          ...analyticsAttributes,
          reason: 'unknown',
          description,
        };
        dispatch(requestCheckUsernameError({ code: 'unknown' }));
      }
    }

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: analyticsAction,
      subject: 'sli',
      attributes: analyticsAttributes,
    });
  };

export const checkUsername =
  ({
    username,
    params,
    analyticsClient,
    recoveryToken,
    challengeResponse,
    destinationOrgId,
    onShowAwsCaptchaModal,
  }: UsernameCheckParams): ThunkAction =>
  dispatch => {
    analyticsClient.operationalEvent({
      page: loginPageId,
      action: 'checkUsernameStart',
      subject: 'sli',
    });

    return dispatch(
      usernameCheck({
        username,
        params,
        analyticsClient,
        recoveryToken,
        challengeResponse,
        destinationOrgId,
        onShowAwsCaptchaModal,
      })
    );
  };

export const isolatedCloudCheckUsername =
  ({
    username,
    params,
    analyticsClient,
    recoveryToken,
    challengeResponse,
    destinationOrgId,
    onShowAwsCaptchaModal,
  }: UsernameCheckParams): ThunkAction =>
  async (dispatch, getState) => {
    dispatch(requestIsolatedCloudCheckUsername());

    addBreadcrumb({
      category: 'login.check_username',
      message: 'Check isolated cloud username API call initiated',
      level: 'info',
      data: {
        hasRecoveryToken: !!recoveryToken,
        hasChallengeResponse: !!challengeResponse,
        challengeType: challengeResponse?.type,
      },
    });

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: 'checkIsolatedCloudUsernameStart',
      subject: 'sli',
    });

    const csrfToken = getState().csrfToken;

    let response: Response;
    try {
      response = await fetch(`/rest/v2/check-username${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          username,
          recoveryToken,
          'challenge-response': toChallengeRequest(challengeResponse),
          destinationOrgId,
        }),
      });
    } catch (networkError) {
      addBreadcrumb({
        category: 'login.check_username',
        message: 'Network error during check-username Isolated Cloud',
        level: 'error',
        data: { isOnline: navigator.onLine },
      });

      captureTaggedException(networkError, {
        capabilities: [
          'capability-group--login',
          'capability-subgroup--username-password-login',
          'capability-name--login-with-username-password',
        ],
        extraProperties: {
          description: 'Network failure during check-username Isolated Cloud API call',
          isOnline: navigator.onLine,
          endpoint: '/rest/v2/check-username',
        },
      });

      dispatch(requestCheckUsernameError({ code: 'request_error' }));
      return;
    }

    let analyticsAction:
      | 'checkIsolatedCloudUsernameSuccessful'
      | 'checkIsolatedCloudUsernameFailure';
    let analyticsAttributes = {} as AnalyticsFailureAttributes;

    if (response.ok) {
      try {
        const userActions: UserAction[] = await response.json();
        dispatch(requestIsolatedCloudCheckUsernameSucceeded(userActions));

        if (userActions.length === 1) {
          if (userActions[0].action === 'not_permitted') {
            // If the only action is not_permitted, show the error immediately (like V1)
            dispatch(requestCheckUsernameError({ code: userActions[0].error_code }));
          } else {
            // If there is only a single user action, automatically select it
            dispatch(
              requestCheckUsernameSucceeded(
                {
                  username,
                  params,
                  analyticsClient,
                  recoveryToken,
                  challengeResponse,
                  onShowAwsCaptchaModal,
                },
                userActions[0]
              )
            );
            dispatch(selectCheckUsernameAccount(userActions[0]));

            setContext('login.checkUsername', {
              action: userActions[0]?.action,
              redirectType: userActions[0]?.action === 'redirect' && userActions[0]?.redirect_type,
              social: userActions[0]?.action === 'no_action' && userActions[0]?.social,
              hasRecoveryToken: !!recoveryToken,
              challengeType: challengeResponse?.type || null,
            });
          }
        }

        addBreadcrumb({
          category: 'login.check_username',
          message: 'Check isolated cloud username succeeded',
          level: 'info',
          data: {
            userActionsCount: userActions.length,
          },
        });

        analyticsAction = 'checkIsolatedCloudUsernameSuccessful';
      } catch (parseError) {
        dispatch(requestCheckUsernameError({ code: 'unknown' }));
        analyticsAction = 'checkIsolatedCloudUsernameFailure';
        analyticsAttributes = {
          ...analyticsAttributes,
          reason: 'response_parsing_error',
        };
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Check-username Isolated Cloud response OK but failed to parse it',
          },
        });
      }
    } else {
      analyticsAction = 'checkIsolatedCloudUsernameFailure';
      let responseErrorText: string | null = null;

      addBreadcrumb({
        category: 'login.check_username',
        message: 'Check isolated cloud username HTTP error',
        level: 'error',
        data: { statusCode: response.status },
      });

      try {
        analyticsAttributes = {
          ...analyticsAttributes,
          reason: 'http_error',
          httpStatus: response.status,
        };

        if (response.status === 429) {
          dispatch(requestCheckUsernameError({ code: 'too_many_username_requests' }));
        } else if (response.status === 400) {
          const { uiErrors } = await response.json();
          if (uiErrors && uiErrors.length) {
            dispatch(requestCheckUsernameError({ code: uiErrors[0].key || 'unknown' }));
          }
        } else {
          responseErrorText = (await response.text()) || response.statusText;
          const extractedError: LoginFieldError['email'] =
            JSON.parse(responseErrorText).formErrors[0];
          analyticsAttributes = {
            ...analyticsAttributes,
            description: extractedError,
          };
          dispatch(requestCheckUsernameFailed(extractedError));
        }
      } catch (parseError) {
        const description = 'Failed to handle non-OK response from check-username Isolated Cloud';
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description,
            responseStatus: response.status,
            responseErrorText,
          },
        });
        analyticsAttributes = {
          ...analyticsAttributes,
          reason: 'unknown',
          description,
        };
        dispatch(requestCheckUsernameError({ code: 'unknown' }));
      }
    }

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: analyticsAction,
      subject: 'sli',
      attributes: analyticsAttributes,
    });
  };

export const isolatedCloudCheckUsernameContinueRequired =
  ({
    username,
    params,
    analyticsClient,
    recoveryToken,
    challengeResponse,
    destinationOrgId,
  }: UsernameCheckParams): ThunkAction =>
  async (dispatch, getState) => {
    const csrfToken = getState().csrfToken;

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: 'checkIsolatedCloudUsernameContinueRequiredStart',
      subject: 'sli',
    });

    let analyticsAction:
      | 'checkIsolatedCloudUsernameContinueRequiredSuccessful'
      | 'checkIsolatedCloudUsernameContinueRequiredFailure';
    let response: Response;
    try {
      response = await fetch(`/rest/v2/check-username/continue${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          username,
          recoveryToken,
          'challenge-response': toChallengeRequest(challengeResponse),
          destinationOrgId,
        }),
      });
    } catch (networkError) {
      analyticsClient.operationalEvent({
        page: loginPageId,
        action: 'checkIsolatedCloudUsernameContinueRequiredFailure',
        subject: 'sli',
      });
      dispatch(requestCheckUsernameError({ code: 'request_error' }));
      return;
    }

    if (response.ok) {
      const userAction = (await response.json()) as RedirectUserAction;
      dispatch(requestCheckUsernameRedirect(userAction));
      analyticsAction = 'checkIsolatedCloudUsernameContinueRequiredSuccessful';
    } else {
      let responseErrorText: string | null = null;
      try {
        if (response.status === 429) {
          dispatch(requestCheckUsernameError({ code: 'too_many_username_requests' }));
        } else if (response.status === 400) {
          const { uiErrors } = await response.json();
          if (uiErrors && uiErrors.length) {
            dispatch(requestCheckUsernameError({ code: uiErrors[0].key || 'unknown' }));
          }
        } else {
          responseErrorText = (await response.text()) || response.statusText;
          const extractedError: LoginFieldError['email'] =
            JSON.parse(responseErrorText).formErrors[0];
          dispatch(requestCheckUsernameFailed(extractedError));
        }
      } catch (parseError) {
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Failed to handle non-OK response from check-username Isolated Cloud',
            responseStatus: response.status,
            responseErrorText,
          },
        });
      }
      analyticsAction = 'checkIsolatedCloudUsernameContinueRequiredFailure';
    }

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: analyticsAction,
      subject: 'sli',
    });
  };

function reportFrontendError(error: Object & { error_description?: string }) {
  function removeField(obj: Object, fieldName: string) {
    for (const prop in obj) {
      if (prop === fieldName) {
        delete obj[prop];
      } else if (typeof obj[prop] === 'object') {
        removeField(obj[prop], fieldName);
      }
    }
  }

  function decodeOriginalResponseText(obj: Object) {
    const original = obj['original'];
    const response = original?.['response'];
    const val = response?.['text'];
    if (val !== 'undefined') {
      try {
        response['text'] = JSON.parse(val);
      } catch (_) {}
    }
  }

  removeField(error, 'password');
  decodeOriginalResponseText(error);
  const errorCode = error['error'];
  captureTaggedException(error, {
    capabilities: [
      'capability-group--login',
      'capability-subgroup--username-password-login',
      'capability-name--login-with-username-password',
    ],
    extraProperties: {
      description: error?.error_description || 'Failed to login for an unexpected reason',
      code: errorCode,
    },
  });
}

function parseAdditionalData(err: any) {
  try {
    return JSON.parse(atob(err.error_description));
  } catch (e) {
    return {};
  }
}

function handleLoginError(
  err: Object,
  username: string,
  application: string | undefined,
  dispatch: Dispatch,
  getState: () => State,
  onLoginError: (string: string) => void
) {
  const additionalData = parseAdditionalData(err);
  const code = (additionalData['custom_error'] || err['error'] || '').toLowerCase();
  const isBitbucket = application && application.toLowerCase() === 'bitbucket';

  addBreadcrumb({
    category: 'login.password_login',
    message: 'Password login failed',
    level: 'error',
    data: {
      errorCode: code,
      hasApplication: !!application,
      isBitbucket,
      attemptNumber: getState().login.nextAttempt,
    },
  });

  if (code === 'too_many_attempts' || code === 'invalid_captcha') {
    dispatch(updateCaptchaRequirement(true));
    dispatch(requestUsernamePasswordLoginFailed({ code: 'captcha' }));
  } else if (code === 'access_denied') {
    dispatch(updateLoginAttemptDetails(getState().login.nextAttempt + 1));
    dispatch(requestUsernamePasswordLoginFailed({ code: 'invalid_user_password' }));
  } else if (code === 'ambiguous_user') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'ambiguous_user' }));
  } else if (code === 'no_email_on_remote_user') {
    dispatch(
      requestUsernamePasswordLoginFailed({
        code: 'no_email_on_remote_user',
        args: { username: username },
      })
    );
  } else if (code === 'username_is_not_email') {
    const errorCode = isBitbucket ? 'bitbucket_username_is_not_email' : 'username_is_not_email';
    dispatch(
      requestUsernamePasswordLoginFailed({
        code: errorCode,
        args: { username: additionalData['email'] },
      })
    );
  } else if (code === 'email_is_not_primary') {
    const errorCode = isBitbucket ? 'bitbucket_email_is_not_primary' : 'email_is_not_primary';
    dispatch(
      requestUsernamePasswordLoginFailed({
        code: errorCode,
        args: { username: additionalData['email'] },
      })
    );
  } else if (code === 'too_many_logins') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'too_many_logins' }));
  } else if (code === 'password_leaked') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'password_leaked' }));
  } else if (code === 'blocked_user') {
    const redirectURL = application
      ? `/login/inactive?application=${application}`
      : '/login/inactive';
    dispatch(redirectToUserInactivePage(redirectURL));
  } else if (code === 'client_ip_not_allowed') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'client_ip_not_allowed' }));
  } else if (code === 'client_ip_missing') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'client_ip_missing' }));
  } else if (code === 'request_error') {
    // 'request_error' auth0-js: connection fails due to offline or cross domain issue
    dispatch(requestUsernamePasswordLoginFailed({ code: 'request_error' }));
  } else if (code === 'invalid.session.token') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'invalid.session.token' }));
  } else {
    reportFrontendError(err);
    dispatch(requestUsernamePasswordLoginFailed({ code: 'unknown' }));
  }

  if (code !== 'too_many_attempts' && code !== 'invalid_captcha') {
    dispatch(updateCaptchaRequirement(false));
  }

  onLoginError(code);
}

function verifyCaptchaIfRequired(captchaValue: string | null | undefined, username: string) {
  if (!captchaValue) {
    return Promise.resolve();
  }

  addBreadcrumb({
    category: 'login.captcha',
    message: 'Verifying captcha response',
    level: 'info',
  });

  return fetch('/rest/captcha/verify', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      'g-recaptcha-response': captchaValue,
      email: username,
    }),
  })
    .then(rejectNonOkResponse)
    .catch(() => {
      addBreadcrumb({
        category: 'login.captcha',
        message: 'Captcha verification failed',
        level: 'error',
      });

      return Promise.reject(new Error('invalid_captcha'));
    });
}

export const prepareState = (
  csrfToken: string,
  config: MarketingConsentConfig | null | undefined,
  marketingConsentState: ConsentCheckboxState,
  anonymousId?: string,
  userFlow?: UserFlow
): LoginState => {
  let state: LoginState = { csrfToken, anonymousId, userFlow };
  const marketingConsent = buildMarketingConsent(config, marketingConsentState);
  if (marketingConsent) {
    state = { ...state, marketingConsent };
  }
  return state;
};

interface LoginParams {
  username: string;
  password: string;
  captchaValue: string | null | undefined;
  location: Location;
  onLoginError: (code: string) => void;
  marketingConsent: ConsentCheckboxState;
  anonymousId: string;
}

export const loginUsernamePassword =
  (loginParams: LoginParams, navigate: (url: string) => void): ThunkAction =>
  (dispatch, getState) => {
    const { location, username, password, captchaValue, onLoginError, anonymousId } = loginParams;
    const params: URLParameters = queryString.parse(location.search);
    const { application, userFlow } = params;
    const state = getState();
    const { isRememberMeChecked } = state.login;
    const { csrfToken, marketingConsent } = state;

    dispatch(requestUsernamePasswordLogin());

    addBreadcrumb({
      category: 'login.password_login',
      message: 'Password login initiated',
      level: 'info',
      data: {
        hasCaptcha: !!captchaValue,
        isRememberMeChecked,
        hasApplication: !!application,
        hasUserFlow: !!userFlow,
      },
    });

    setContext('login.passwordLogin', {
      hasCaptcha: !!captchaValue,
      isRememberMeChecked,
      application,
      userFlow,
      attemptNumber: state.login.nextAttempt,
    });

    const loginState = prepareState(
      csrfToken,
      marketingConsent.config,
      loginParams.marketingConsent,
      anonymousId,
      userFlow
    );

    // This passes the `remember` flag to the backend, "remembering" the account that we're logging into.
    if (isRememberMeChecked) {
      params.remember = 'true';
    }

    const query = queryString.stringify(params);

    const loginWithAtlassianAuth = (navigate: (url: string) => void) =>
      login({
        username,
        password,
        state: loginState,
        query: `?${query}${location.hash}`,
      }).then(result => {
        switch (result.kind) {
          case 'failure': {
            const err = { error: result.reason };
            return handleLoginError(err, username, application, dispatch, getState, onLoginError);
          }
          case 'success':
            addBreadcrumb({
              category: 'login.password_login',
              message: 'Password login succeeded',
              level: 'info',
            });
            return navigate(result.redirectUri);
        }
      });

    return verifyCaptchaIfRequired(captchaValue, username)
      .then(() => loginWithAtlassianAuth(navigate))
      .catch(err => {
        captureTaggedException(err, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Failed to login',
          },
        });
        handleLoginError(err, username, application, dispatch, getState, onLoginError);
      });
  };

interface PasswordlessLoginParams {
  email: string;
  captchaValue: string | null | undefined;
  location: Location;
}

export const loginPasswordless =
  ({
    email,
    captchaValue,
    location,
  }: PasswordlessLoginParams): ThunkAction<Promise<{ success: boolean }>> =>
  async (dispatch, getState) => {
    const {
      csrfToken,
      login: { isRememberMeChecked },
    } = getState();

    dispatch(requestPasswordlessLogin());

    addBreadcrumb({
      category: 'login.passwordless',
      message: 'Passwordless login initiated',
      level: 'info',
      data: {
        hasCaptcha: !!captchaValue,
        isRememberMeChecked,
      },
    });

    setContext('login.passwordlessLogin', {
      hasCaptcha: !!captchaValue,
      isRememberMeChecked,
    });

    const urlParams = new URLSearchParams(location.search);

    if (isRememberMeChecked) {
      urlParams.set('remember', 'true');
    }

    const apiParams = urlParams.toString();

    try {
      const response = await fetch(`/rest/login/passwordless?${apiParams}`, {
        method: 'POST',
        headers: {
          'X-CSRF-TOKEN': csrfToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, captchaValue }),
      });

      const { csrfToken: newCsrfToken } = await response.json();
      if (newCsrfToken) {
        dispatch(updateCsrfToken(newCsrfToken));
      }

      if (response.ok) {
        addBreadcrumb({
          category: 'login.passwordless',
          message: 'Passwordless login succeeded',
          level: 'info',
        });
        dispatch(requestPasswordlessSucceeded());
        return { success: true };
      } else {
        addBreadcrumb({
          category: 'login.passwordless',
          message: 'Passwordless login failed',
          level: 'error',
          data: { statusCode: response.status },
        });
        dispatch(requestPasswordlessFailed({ code: 'unknown' }));
        return { success: false };
      }
    } catch (error) {
      captureTaggedException(error, {
        capabilities: [
          'capability-group--login',
          'capability-subgroup--passwordless-login',
          'capability-name--passwordless-login',
        ],
        extraProperties: {
          description: 'Failed passwordless login',
        },
      });
      dispatch(requestPasswordlessFailed({ code: 'request_error' }));
      return { success: false };
    }
  };
